<script setup lang="ts">
import { computed, ref } from "vue";

import { internalActions } from "@/action/internalActions";
import { linkActions } from "@/action/linkActions";
import { cardKey, cardMetaKey } from "@/components/StickyNote/injectKeys";
import { boardKey } from "@/components/board/injectKeys";
import LinkDragHandle from "@/components/linkDrag/LinkDragHandle.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { useLinkDragSource } from "@/composables/linkDrag";
import { sendStickyNoteAction } from "@/composables/useEventBus";
import { normalLinkColorForBoard } from "@/model/colors";
import type { BoardCoordinate } from "@/model/coordinates";
import { useBoardStore } from "@/store/board";
import { useDraggingStore } from "@/store/dragging";
import { resetCardMark } from "@/store/helpers/mark";
import { useLinkStore } from "@/store/link";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { injectStrict } from "@/utils/vue";

import { findDeleteIconPos } from "./utils";

const props = defineProps<{ scale: number; offset: BoardCoordinate }>();

const card = injectStrict(cardKey);
const cardMeta = injectStrict(cardMetaKey);
const board = injectStrict(boardKey);

const isDragging = computed(() => {
  const dragging = useDraggingStore().dragging;
  return !!dragging[card.id] || card.linkedStickies.some((link) => !!dragging[link.stickyId]);
});

const links = computed(() => {
  return card.linkedStickies.flatMap((link) => {
    const matches = useSearchMenuStore().matchesLinkStates(
      useLinkStore().cardLinkState(card, link),
      true,
    );
    if (matches) {
      const linkedCard = useLinkStore().boardCardByLink(link.stickyId, board.value);
      if (linkedCard) {
        const deleteIconPos = findDeleteIconPos(
          cardMeta.pos,
          props.scale,
          linkedCard.meta.pos,
          props.offset,
        );
        if (deleteIconPos) {
          return { linkId: link.linkId, ...deleteIconPos, to: link.stickyId };
        }
      }
    }
    return [];
  });
});

const removeLink = (link: (typeof links.value)[number]) => {
  void linkActions.remove("link-remove-button", link.linkId, useLinkStore().linksById[link.linkId]);
};

// Shrink an enlarged sticky before its drag, re-enlarge on drag end.
const enlargeAfterLinking = ref(false);

const { handleProps: linkDragHandleProps } = useLinkDragSource(
  () => ({ kind: "sticky", boardId: board.value.id, id: card.id }),
  {
    color: normalLinkColorForBoard(board.value.type),
    // Look up by id — cardMeta.rootEl is captured at setup, before mount.
    anchorEl: () => document.getElementById(card.id),
    onStart: () => {
      if (useBoardStore().enlargedStickyNoteId === card.id) {
        enlargeAfterLinking.value = true;
        useBoardStore().setEnlargedStickyNoteId(null);
      }
    },
    onDragEnd: () => {
      if (enlargeAfterLinking.value) {
        enlargeAfterLinking.value = false;
        sendStickyNoteAction(card.id, {
          action: "enlarge",
          focus: true,
          trigger: "after-link-drag",
        });
      }
    },
    onDrop: async (source, target) => {
      if (target.kind === "sticky") {
        await linkActions.add("drag", { fromId: source.id, toId: target.id });
        resetCardMark(useBoardStore().currentBoard().cards[target.id]);
        internalActions.updateCardLinkedMarks();
      } else if (target.kind === "objective") {
        await linkActions.addObjective("drag", source.id, target.boardId, target.id);
      }
    },
  },
);
</script>

<template>
  <template v-if="!isDragging && !cardMeta.isReadonly">
    <SvgIcon
      v-for="link in links"
      :key="link.linkId"
      name="system/minus-circled"
      data-no-screenshot
      data-no-animate
      width="2em"
      height="2em"
      class="link-remove"
      :style="{ left: link.x + 'px', top: link.y + 'px' }"
      :title="$t('linkModal.removeLink')"
      @click.stop="removeLink(link)"
    />
  </template>

  <LinkDragHandle
    v-if="!isDragging && !cardMeta.isReadonly"
    v-bind="linkDragHandleProps"
    class="link-drag-position"
  />
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";

.link-remove {
  position: absolute;
  margin-left: -1em;
  margin-top: -1em;
  cursor: pointer;
  color: colors.$destructive;
  background-color: colors.$background-ui;
  border-radius: 50%;
}

.link-drag-position {
  position: absolute;
  left: 45%;
  bottom: -38px;
  width: 20px;
  pointer-events: all;

  .sticky-note-progress ~ & {
    bottom: -49px;
  }
}
</style>
